import {
  type AppDependency,
  type DeployAppRow,
  fetchApps,
  fetchEnvironmentById,
  fetchEnvironmentServices,
  fetchEnvironments,
  fetchImageById,
  fetchServices,
  selectAppsForTable,
  selectAppsForTableByCertId,
  selectAppsForTableByEnvId,
  selectAppsForTableByExternalAwsResourceId,
  selectAppsForTableBySourceId,
  selectImageById,
} from "@app/deploy";
import { fetchDeployments } from "@app/deployment";
import {
  useCompositeLoader,
  useLoader,
  useQuery,
  useSelector,
} from "@app/react";
import {
  appDetailUrl,
  deployUrl,
  deploymentDetailUrl,
  environmentCreateAppUrl,
} from "@app/routes";
import type { DeployApp } from "@app/types/deploy";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ButtonLink } from "../button";
import { Code } from "../code";
import { costColDef } from "../cost-estimate-tooltip";
import { DataTable, idColDef } from "../datatable";
import {
  AppCell,
  CurrencyCell,
  DateTimeCell,
  EnvironmentCell,
  ServicesCell,
} from "../datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "../datatable/datatable";
import { DockerImage } from "../docker";
import { GitCommitMessage, GitRef } from "../git";
import { Group } from "../group";
import { IconPlusCircle } from "../icons";
import { LastDeployAtDisplay, LastDeployNeverDisplay } from "../last-deploy-at";
import { TitleBar } from "../resource-list-view";
import { tokens } from "../tokens";
import { Tooltip } from "../tooltip";

export const AppItemView = ({ app }: { app: DeployApp }) => {
  return (
    <Link to={appDetailUrl(app.id)} className="flex">
      <img
        src="/resource-types/logo-app.png"
        className="w-[32px] h-[32px] mr-2 align-middle"
        aria-label="App"
      />
      <p className={`${tokens.type["table link"]} leading-8`}>{app.handle}</p>
    </Link>
  );
};

// ─── Shared column definitions ───────────────────────────────────────────────

const colDefs: Record<string, DataTableColDef<DeployAppRow>> = {
  handle: {
    field: "handle",
    headerName: "Handle",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <AppCell id={data.id} />
    ),
  },
  id: {
    field: "id",
    ...idColDef,
    sort: "desc",
    valueGetter: ({ data }) => Number.parseInt(data?.id || "0", 10),
  },
  environment: {
    field: "envHandle",
    headerName: "Environment",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <EnvironmentCell id={data.environmentId} hideIcon={true} />
    ),
  },
  services: {
    field: "totalServices",
    headerName: "Services",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <ServicesCell services={data.services} />
    ),
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  cost: {
    field: "cost",
    ...costColDef,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <CurrencyCell value={data.cost} />
    ),
  },
  gitRef: {
    field: "gitRef",
    headerName: "Git Ref",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <GitRef
        gitRef={data.currentDeployment.gitRef}
        commitSha={data.currentDeployment.gitCommitSha}
        commitUrl={data.currentDeployment.gitCommitUrl}
      />
    ),
  },
  commitMessage: {
    field: "gitCommitSha",
    headerName: "Commit Message",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <GitCommitMessage message={data.currentDeployment.gitCommitMessage} />
    ),
  },
  dockerImage: {
    field: "dockerImageName",
    headerName: "Docker Image",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => {
      useQuery(fetchImageById({ id: data.currentImageId }));
      const image = useSelector((s) =>
        selectImageById(s, { id: data.currentImageId }),
      );
      return (
        <DockerImage
          image={data.currentDeployment.dockerImage || image.dockerRepo}
          digest={image.dockerRef}
          repoUrl={data.currentDeployment.dockerRepositoryUrl}
        />
      );
    },
  },
  lastDeployed: {
    field: "lastDeployed",
    headerName: "Last Deploy (UTC)",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => {
      const loader = useLoader(fetchDeployments);
      if (loader.isInitialLoading || loader.isLoading)
        return (
          <div className="flex items-center gap-1.5">
            <span className={tokens.type["table lead text"]}>{"-"}</span>
          </div>
        );
      return data.lastDeployed ? (
        <LastDeployAtDisplay at={data.lastDeployed} />
      ) : (
        <LastDeployNeverDisplay unknown={!!data?.deletedAt} />
      );
    },
  },
  deploymentActions: {
    field: "id",
    headerName: "Actions",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployAppRow>) => (
      <ButtonLink
        to={deploymentDetailUrl(data.currentDeployment.id)}
        size="sm"
        variant="primary"
      >
        View
      </ButtonLink>
    ),
  },
};

const appListByOrgColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.environment,
  colDefs.services,
  colDefs.lastDeployed,
  colDefs.createdAt,
  colDefs.cost,
];

export const AppListByOrg = () => {
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchApps,
    fetchEnvironments,
    fetchServices,
    fetchDeployments,
  ]);
  const apps = useSelector((s) => selectAppsForTable(s, {}));

  return (
    <Group size="xl">
      <Group variant="horizontal" className="items-center justify-between">
        <TitleBar description="Apps are how you deploy your code, scale services, and manage endpoints.">
          Apps
        </TitleBar>
        <ButtonLink to={deployUrl()}>
          <IconPlusCircle variant="sm" className="mr-2" /> New App
        </ButtonLink>
      </Group>

      <DataTable<DeployAppRow>
        rowData={apps}
        syncQueryParams={true}
        colDefs={appListByOrgColDefs}
        isLoading={!apps.length && (isLoading || isInitialLoading)}
      />
    </Group>
  );
};

const appListByEnvironmentColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.services,
  colDefs.cost,
];

export const AppListByEnvironment = ({ envId }: { envId: string }) => {
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchApps,
    fetchEnvironmentById({ id: envId }),
    fetchEnvironmentServices({ id: envId }),
    fetchDeployments,
  ]);
  const apps = useSelector((s) => selectAppsForTableByEnvId(s, { envId }));

  return (
    <Group size="lg">
      <div className="flex justify-end">
        <ButtonLink to={environmentCreateAppUrl(envId)}>
          <IconPlusCircle variant="sm" className="mr-2" /> New App
        </ButtonLink>
      </div>

      <DataTable<DeployAppRow>
        rowData={apps}
        colDefs={appListByEnvironmentColDefs}
        isLoading={!apps.length && (isLoading || isInitialLoading)}
      />
    </Group>
  );
};

const appListByCertificateColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.environment,
  colDefs.services,
  colDefs.cost,
];

export const AppListByCertificate = ({
  certId,
  envId,
}: {
  certId: string;
  envId: string;
}) => {
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchApps,
    fetchEnvironmentById({ id: envId }),
    fetchEnvironmentServices({ id: envId }),
    fetchDeployments,
  ]);
  const apps = useSelector((s) =>
    selectAppsForTableByCertId(s, { certId, envId }),
  );

  return (
    <DataTable<DeployAppRow>
      rowData={apps}
      colDefs={appListByCertificateColDefs}
      isLoading={!apps.length && (isLoading || isInitialLoading)}
    />
  );
};

const appListByExternalAwsResourceColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.environment,
  colDefs.services,
  colDefs.cost,
];

export const AppListByExternalAwsResource = ({
  resourceId,
}: {
  resourceId: string;
}) => {
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchApps,
    fetchEnvironments,
  ]);
  const apps = useSelector((s) =>
    selectAppsForTableByExternalAwsResourceId(s, { resourceId }),
  );

  return (
    <DataTable<DeployAppRow>
      rowData={apps}
      colDefs={appListByExternalAwsResourceColDefs}
      isLoading={!apps.length && (isLoading || isInitialLoading)}
    />
  );
};

const appListBySourceColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.gitRef,
  colDefs.commitMessage,
  colDefs.dockerImage,
  colDefs.lastDeployed,
  colDefs.deploymentActions,
];

export const AppListBySource = ({ sourceId }: { sourceId: string }) => {
  const { isLoading, isInitialLoading } = useQuery(fetchApps());
  const apps = useSelector((s) =>
    selectAppsForTableBySourceId(s, { sourceId }),
  );

  return (
    <DataTable<DeployAppRow>
      rowData={apps}
      colDefs={appListBySourceColDefs}
      isLoading={!apps.length && (isLoading || isInitialLoading)}
    />
  );
};

type AppDependencyRow = DeployAppRow & { why?: string };

const appListByDependencyColDefs: DataTableColDef<AppDependencyRow>[] = [
  colDefs.handle,
  colDefs.id,
  colDefs.environment,
  colDefs.services,
  colDefs.cost,
  {
    field: "why",
    headerName: "Reason",
    cellRenderer: ({ data }: DataTableCellRendererProps<AppDependencyRow>) =>
      data.why ? (
        <Tooltip placement="left" text={data.why} fluid>
          <Code>{data.why}</Code>
        </Tooltip>
      ) : null,
  },
];

export const AppDependencyList = ({ apps }: { apps: AppDependency[] }) => {
  const allTableApps = useSelector((s) => selectAppsForTable(s, {}));

  const rows = useMemo<AppDependencyRow[]>(
    () =>
      apps.flatMap((dep) => {
        const row = allTableApps.find((r) => r.id === dep.resource.id);
        if (!row) return [];
        return [{ ...row, why: dep.why }];
      }),
    [apps, allTableApps],
  );

  return (
    <DataTable<AppDependencyRow>
      rowData={rows}
      colDefs={appListByDependencyColDefs}
      pageSize={25}
    />
  );
};
